import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Lightbulb, PlaneLanding, PlaneTakeoff, AlertTriangle, TrendingUp, DollarSign, Loader2, RefreshCw, ChevronDown, ChevronUp, X, ClipboardList, CheckCircle2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/lib/queryClient";

interface Briefing {
  greeting: string;
  summary: string;
  checkIns: { count: number; names: string[] };
  checkOuts: { count: number };
  urgentItems: string[];
  stats: { revenue30d: number; occupancyRate: string; activeReservations: number; unpaidAmount: number };
  serviceStats?: { completedJobs: number; pendingJobs: number; grossIncome: number; cleanerPayroll: number };
  tip: string;
}

interface LumiBriefingProps {
  businessMode?: "property" | "services";
  apiEndpoint?: string;
}

export default function LumiBriefing({ businessMode = "property", apiEndpoint }: LumiBriefingProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [dismissedItems, setDismissedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    setDismissedItems(new Set());
  }, [businessMode]);

  const baseUrl = apiEndpoint || "/api/admin/lumi/briefing";

  const { data: briefing, isLoading, error } = useQuery<Briefing>({
    queryKey: [baseUrl, businessMode],
    queryFn: async () => {
      const res = await fetch(`${baseUrl}?mode=${businessMode}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load briefing");
      return res.json();
    },
    staleTime: 4 * 60 * 60 * 1000,
    gcTime: 8 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <Card className="border-none shadow-sm bg-gradient-to-r from-amber-50/80 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10">
        <CardContent className="p-4 sm:p-5 flex items-center gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm shrink-0">
            <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-spin" />
          </div>
          <div>
            <p className="text-sm font-medium">Lumi is preparing your briefing...</p>
            <p className="text-xs text-muted-foreground">Analyzing your live business data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !briefing) return null;

  return (
    <Card className="border-none shadow-sm bg-gradient-to-r from-amber-50/80 to-orange-50/40 dark:from-amber-950/20 dark:to-orange-950/10" data-testid="lumi-briefing-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-start gap-2 sm:gap-3 text-left flex-1 min-w-0"
            data-testid="btn-toggle-briefing"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm shrink-0 mt-0.5">
              <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h3 className="text-sm font-bold truncate" data-testid="briefing-greeting">{briefing.greeting}</h3>
                {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1" data-testid="briefing-summary">{briefing.summary}</p>
            </div>
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => { setDismissedItems(new Set()); queryClient.invalidateQueries({ queryKey: [baseUrl] }); }}
            data-testid="btn-refresh-briefing"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {!collapsed && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-3">
              {businessMode === 'services' && briefing.serviceStats ? (
                <>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-completed-jobs">{briefing.serviceStats.completedJobs}</p>
                      <p className="text-[10px] text-muted-foreground">Jobs Done</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <ClipboardList className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-pending-jobs">{briefing.serviceStats.pendingJobs}</p>
                      <p className="text-[10px] text-muted-foreground">Pending</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-gross-income">${((briefing.serviceStats.grossIncome || 0) / 1000).toFixed(1)}k</p>
                      <p className="text-[10px] text-muted-foreground">30d Income</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-payroll">${((briefing.serviceStats.cleanerPayroll || 0) / 1000).toFixed(1)}k</p>
                      <p className="text-[10px] text-muted-foreground">Payroll</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <PlaneLanding className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-checkins">{briefing.checkIns?.count || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Check-ins</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <PlaneTakeoff className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-checkouts">{briefing.checkOuts?.count || 0}</p>
                      <p className="text-[10px] text-muted-foreground">Check-outs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-occupancy">{briefing.stats?.occupancyRate || '0'}%</p>
                      <p className="text-[10px] text-muted-foreground">Occupancy</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-2.5 sm:px-3 py-2 rounded-lg bg-white/60 dark:bg-white/5 border border-white/40 dark:border-white/10">
                    <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-base sm:text-lg font-bold leading-tight" data-testid="briefing-revenue">${((briefing.stats?.revenue30d || 0) / 1000).toFixed(1)}k</p>
                      <p className="text-[10px] text-muted-foreground">30d Revenue</p>
                    </div>
                  </div>
                </>
              )}
            </div>

            {briefing.urgentItems && briefing.urgentItems.filter((_, i) => !dismissedItems.has(i)).length > 0 && (
              <div className="space-y-1.5 mt-3">
                {briefing.urgentItems.map((item, i) => {
                  if (dismissedItems.has(i)) return null;
                  return (
                    <div key={i} className="flex items-start gap-2 text-xs group">
                      <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                      <span className="text-muted-foreground flex-1" data-testid={`briefing-urgent-${i}`}>{item}</span>
                      <button
                        onClick={() => setDismissedItems(prev => new Set(prev).add(i))}
                        className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-0.5 rounded hover:bg-muted"
                        data-testid={`btn-dismiss-urgent-${i}`}
                      >
                        <X className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {briefing.tip && (
              <div className="px-3 py-2 rounded-lg bg-amber-100/50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/30 mt-3">
                <p className="text-xs text-amber-800 dark:text-amber-300" data-testid="briefing-tip">
                  <span className="font-semibold">Tip:</span> {briefing.tip}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
